import React, { useState } from 'react';
import {
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  Fade,
  Grow,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  Link,
} from '@mui/material';
import { 
  // CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  School as StudentIcon,
  Campaign as ClubIcon,
  AdminPanelSettings as AdminIcon,
  // Security as SecurityIcon,
  // SupportAgent as SupportIcon,
  // MonetizationOn as FreeIcon
} from '@mui/icons-material';

// Import your components
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import AuthStepper from '../../components/AuthStepper';

// Import CSS styles
import '../../styles/Login.css';

const LoginRoleSelection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const roles = [
    {
      id: 'student',
      title: 'Student',
      subtitle: 'Learn, Connect & Grow',
      description: 'Join study groups, access doubt clearing, connect with peers, and enhance your college experience.',
      iconComponent: StudentIcon,
      iconEmoji: '👨‍🎓',
      color: '#1E3A8A',
      lightColor: '#EAF2FF',
      gradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
      hoverGradient: 'linear-gradient(135deg, #0D1C4F 0%, #1E3A8A 100%)',
      shadowColor: 'rgba(13, 28, 79, 0.35)',
      ctaText: 'Login as Student',
      route: '/login/student',
      cardBg: 'linear-gradient(135deg, #fafaff 0%, #f0f4ff 100%)',
      popular: true
    },
    {
      id: 'club-member',
      title: 'Club Member',
      subtitle: 'Lead, Organize & Impact',
      description: 'Organize events, manage teams, coordinate activities, and build stronger communities.',
      iconComponent: ClubIcon,
      iconEmoji: '👩‍💼',
      color: '#016f57',
      lightColor: '#E8F7F0',
      gradient: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
      hoverGradient: 'linear-gradient(135deg, #016f57 0%, #019a78 100%)',
      shadowColor: 'rgba(1, 111, 87, 0.35)',
      ctaText: 'Login as Club Member',
      route: '/login/club-member',
      cardBg: 'linear-gradient(135deg, #fafffe 0%, #f0fdf4 100%)'
    },
    {
      id: 'admin',
      title: 'Admin',
      subtitle: 'Manage & Oversee',
      description: 'Platform management, user oversight, system controls, and maintaining community standards.',
      iconComponent: AdminIcon,
      iconEmoji: '🛡️',
      color: '#6b7280',
      lightColor: '#f3f4f6',
      gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
      hoverGradient: 'linear-gradient(135deg, #5b6471 0%, #374151 100%)',
      shadowColor: 'rgba(107, 114, 128, 0.4)',
      ctaText: 'Login as Admin',
      route: '/login/admin',
      cardBg: 'linear-gradient(135deg, #fafafa 0%, #f9fafb 100%)'
    }
  ];

  const handleRoleSelect = (role) => {
    // Analytics tracking
    // if (typeof analytics !== 'undefined') {
    //   analytics.track('role_selected', { role: role.id });
    // }
    
    // Navigate to role-specific login
    setTimeout(() => {
      // Replace with your actual navigation logic
      // For React Router: navigate(role.route);
      // For Next.js: router.push(role.route);
      window.location.href = role.route;
    }, 200);
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      {/* Navbar */}
      <Navbar />
      
      {/* Main Content */}
      <Box className="main-content">
        {/* Progress Indicator */}
        <AuthStepper
          currentStep={1}
          totalSteps={2}
          progressValue={4}
          palette={{
            barGradient: 'linear-gradient(90deg, #016f57, #019a78)',
            barShadow: '0 0 10px rgba(1, 111, 87, 0.25)',
            trackColor: 'rgba(148, 163, 184, 0.2)',
            dotGradient: 'linear-gradient(135deg, #016f57, #019a78)',
            dotShadow: '0 2px 8px rgba(1, 111, 87, 0.25)',
          }}
          containerProps={{ maxWidth: 'md', className: 'progress-container' }}
          paperProps={{ elevation: 0, className: 'progress-paper', sx: { p: { xs: 2, md: 3 } } }}
          progressWrapperProps={{ className: 'progress-wrapper' }}
          progressProps={{ className: 'progress-bar' }}
          dotProps={{ className: 'progress-dot' }}
          content={(
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box className="step-icon-current">
                  ✓
                </Box>
                <Typography variant="body1" className="step-label-current">
                  Choose Your Role
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" className="step-label">
                  Login
                </Typography>
                <Box className="step-icon">
                  2
                </Box>
              </Box>
            </Stack>
          )}
        />

        <Container maxWidth="xl">
          <Fade in={true} timeout={1000}>
            <Box>
              {/* Header */}
              <Box className="header-section">
                <Typography
                  variant={isMobile ? 'h3' : 'h2'}
                  component="h1"
                  className={`main-title ${isMobile ? 'mobile' : ''}`}
                >
                  Choose your role to log in
                </Typography>
                <Typography
                  variant="h5"
                  className="main-subtitle"
                >
                  Your role determines your dashboard and features. Select the option that best describes you.
                </Typography>
              </Box>

              {/* Role Cards */}
              <Container maxWidth={false} className="cards-container">
                <Grid container spacing={3} justifyContent="center">
                  {roles.map((role, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      md={6}
                      lg={4}
                      key={role.id} 
                      className="card-grid-item"
                    >
                      <Grow in={true} timeout={1200 + index * 300}>
                        <Card
                          role="button"
                          tabIndex={0}
                          aria-label={`Select ${role.title} role`}
                          onMouseEnter={() => setHoveredCard(role.id)}
                          onMouseLeave={() => setHoveredCard(null)}
                          onClick={() => handleRoleSelect(role)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRoleSelect(role);
                            }
                          }}
                          className={`role-card role-${role.id}-card ${hoveredCard === role.id ? 'hovered' : ''}`}
                          style={{
                            '--role-color': role.color,
                            '--role-gradient': role.gradient,
                            '--role-hover-gradient': role.hoverGradient,
                            '--role-shadow-color': role.shadowColor,
                            '--role-card-bg': role.cardBg
                          }}
                        >
                          {/* Popular Badge */}
                          {role.popular && (
                            <Chip
                              label="Most Popular"
                              size="small"
                              className="popular-badge"
                            />
                          )}

                          <CardContent className="card-content">
                            {/* Icon and Title Section */}
                            <Box className="role-top">
                              <Avatar
                                className={`role-avatar ${hoveredCard === role.id ? 'hovered' : ''}`}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  m: '0 auto 1.5rem',
                                  fontSize: '2.5rem',
                                  background: role.gradient,
                                  boxShadow: `0 8px 24px ${role.shadowColor}`,
                                  color: 'white'
                                }}
                              >
                                {role.iconEmoji}
                              </Avatar>
                              
                              <Typography
                                variant="h4"
                                component="h2"
                                className="role-title"
                              >
                                {role.title}
                              </Typography>
                              
                              <Typography
                                variant="subtitle1"
                                className="role-subtitle"
                                style={{ color: role.color }}
                              >
                                {role.subtitle}
                              </Typography>
                              
                              <Typography
                                variant="body1"
                                className="role-description"
                              >
                                {role.description}
                              </Typography>
                            </Box>

                            {/* CTA Button */}
                            <Button
                              variant="contained"
                              endIcon={<ArrowIcon className="cta-arrow" />}
                              fullWidth
                              className="cta-button"
                              style={{
                                background: role.gradient,
                                boxShadow: `0 6px 20px ${role.shadowColor}`,
                                '--hover-gradient': role.hoverGradient,
                                '--hover-shadow': `0 8px 25px ${role.shadowColor}`
                              }}
                            >
                              {role.ctaText}
                            </Button>
                          </CardContent>
                        </Card>
                      </Grow>
                    </Grid>
                  ))}
                </Grid>
              </Container>

              

              {/* Statistics Section */}
              {/* <Box className="statistics-section">
                <Typography variant="h5" className="statistics-title">
                  Making real impact across campuses
                </Typography>
                <Stack 
                  direction={isMobile ? 'column' : 'row'} 
                  spacing={4} 
                  justifyContent="center" 
                  alignItems="center"
                >
                  <Paper elevation={0} className="stat-card student-stat">
                    <Typography variant="h3" className="stat-number student-number">
                      1,200+
                    </Typography>
                    <Typography variant="body1" className="stat-label">
                      Active Students
                    </Typography>
                  </Paper>
                  <Paper elevation={0} className="stat-card doubts-stat">
                    <Typography variant="h3" className="stat-number doubts-number">
                      3,000+
                    </Typography>
                    <Typography variant="body1" className="stat-label">
                      Doubts Resolved
                    </Typography>
                  </Paper>
                  <Paper elevation={0} className="stat-card lives-stat">
                    <Typography variant="h3" className="stat-number lives-number">
                      250+
                    </Typography>
                    <Typography variant="body1" className="stat-label">
                      Lives Transformed
                    </Typography>
                  </Paper>
                </Stack>
              </Box> */}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default LoginRoleSelection;